import type {
    ICreateSnapshotMetadata,
    ISnapshotAnnotation,
    ISnapshotResponse,
    IUpdateSnapshot,
} from '@escloud/types';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef } from 'react';
import { toast } from 'sonner';

import { bakeAnnotations } from '@/components/snapshot/annotation';
import type { AnnotationData } from '@/components/snapshot/annotation';
import { useApi } from '@/context';
import { useVisualization } from '@/context/visualization-context';
import { ApiError } from '@/services';
import {
    ViewportSnapshotService,
    getSnapshotBaseUrl,
    type ViewMode,
    type ViewportSnapshot,
} from '@/visualization';

export const snapshotKeys = {
    all: ['snapshots'] as const,
    forCase: (caseId: string) => [...snapshotKeys.all, caseId] as const,
};

const MAX_RETRIES = 2;
const KEY_SEP = '::';

const templateKey = (templateConfigId: string, configId: string): string =>
    `${templateConfigId}${KEY_SEP}${configId}`;

const annotationsOf = (snapshot: ViewportSnapshot): ISnapshotAnnotation[] =>
    snapshot.annotations as unknown as ISnapshotAnnotation[];

/** Retry transient/5xx only; a 4xx (limit/validation/404/413) won't change on retry. */
async function withRetry<T>(fn: () => Promise<T>): Promise<T> {
    let lastErr: unknown;
    for (let attempt = 0; attempt <= MAX_RETRIES; attempt++) {
        try {
            return await fn();
        } catch (err) {
            lastErr = err;
            if (err instanceof ApiError && err.status >= 400 && err.status < 500) throw err;
        }
    }
    throw lastErr;
}

function buildUserMetadata(snapshot: ViewportSnapshot): ICreateSnapshotMetadata {
    return {
        clientId: snapshot.id,
        origin: 'user',
        viewMode: snapshot.viewMode,
        baseVariant: snapshot.baseVariant,
        label: snapshot.label,
        description: snapshot.description,
        annotations: annotationsOf(snapshot),
        includedInReport: snapshot.includedInReport,
        capturedAt: snapshot.capturedAt,
    };
}

function buildTemplateMetadata(
    snapshot: ViewportSnapshot,
    captureSignature: string | undefined
): ICreateSnapshotMetadata {
    return {
        clientId: snapshot.id,
        origin: 'template',
        templateConfigId: snapshot.templateConfigId,
        configId: snapshot.configId,
        captureSignature,
        viewMode: snapshot.viewMode,
        baseVariant: snapshot.baseVariant,
        label: snapshot.label,
        description: snapshot.description,
        annotations: annotationsOf(snapshot),
        includedInReport: snapshot.includedInReport,
        capturedAt: snapshot.capturedAt,
    };
}

function buildPatch(snapshot: ViewportSnapshot): IUpdateSnapshot {
    return {
        description: snapshot.description,
        includedInReport: snapshot.includedInReport,
        annotations: annotationsOf(snapshot),
        baseVariant: snapshot.baseVariant,
    };
}

/**
 * Mirrors the in-session `ViewportSnapshotService` store to the server, and
 * hydrates it on mount. The service stays the single source of truth for the
 * UI; this hook only watches its events and persists.
 *
 * Two persistence shapes:
 * - **User snapshots** — the base PNG image(s) are stored, keyed by `clientId`.
 *   STORED → POST, UPDATED → PATCH, REMOVED → DELETE.
 * - **Template annotations** — the base image is regenerated client-side, so
 *   only the annotations persist, keyed by `templateConfigId::configId`. The
 *   first user edit POSTs (metadata only); later edits PATCH. Every capture pass
 *   emits `TEMPLATE_CAPTURE_RECORDED`, which drives re-application of the saved
 *   annotations onto the freshly regenerated snapshots (or deletes them when the
 *   capture signature has changed — they were anchored to stale data). A template
 *   snapshot's REMOVED event is only ever the capture pass replacing its own
 *   output (the panel does not allow deleting them), so it never DELETEs a row.
 *
 * Best-effort throughout: retry on transient failures, toast on terminal
 * failure, 409 limit keeps a snapshot session-local. Writes are serialised per
 * key so a save → edit can't race.
 */
export function useSnapshotPersistence({ caseId }: { caseId: string }): void {
    const { getServiceManager } = useVisualization();
    const api = useApi();
    const queryClient = useQueryClient();

    const userIdMapRef = useRef<Map<string, string>>(new Map()); // user clientId -> serverId
    const templateMapRef = useRef<Map<string, string>>(new Map()); // configKey -> serverId
    const templateRecordsRef = useRef<Map<string, ISnapshotResponse>>(new Map()); // configKey -> persisted record (in-memory mirror)
    const clientKeyRef = useRef<Map<string, string>>(new Map()); // template store id -> configKey
    const applyingRef = useRef<Set<string>>(new Set()); // store ids being re-applied (suppress echo)
    const chainsRef = useRef<Map<string, Promise<unknown>>>(new Map());
    const hydratedRef = useRef<string | null>(null);
    const prevCaseRef = useRef<string | null>(null);

    // Metadata-only list; images and template re-application happen below.
    const listQuery = useQuery({
        queryKey: snapshotKeys.forCase(caseId),
        queryFn: () => api.snapshots.list(caseId),
        enabled: !!caseId,
        staleTime: Infinity,
    });
    const records = listQuery.data;

    // --- forward sync -------------------------------------------------------
    useEffect(() => {
        const service = getServiceManager().getService<ViewportSnapshotService>(
            ViewportSnapshotService.SERVICE_NAME
        );
        const snapshots = api.snapshots;
        const userIdMap = userIdMapRef.current;
        const templateMap = templateMapRef.current;
        const templateRecords = templateRecordsRef.current;
        const clientKey = clientKeyRef.current;
        const applying = applyingRef.current;
        const chains = chainsRef.current;

        const enqueue = (key: string, task: () => Promise<void>): void => {
            const prev = chains.get(key) ?? Promise.resolve();
            const next = prev.catch(() => undefined).then(task);
            chains.set(key, next);
            void next.finally(() => {
                if (chains.get(key) === next) chains.delete(key);
            });
        };

        const reportFailure = (action: string, err: unknown): void => {
            if (err instanceof ApiError && err.errorCode === 'snapshot_limit_exceeded') {
                const details = err.body?.details as { limit?: number } | undefined;
                toast.warning(
                    `Snapshot limit reached${details?.limit ? ` (${details.limit})` : ''}. This snapshot is kept on this device only.`
                );
                return;
            }
            toast.error(`Could not ${action} snapshot on the server — it remains available locally.`);
        };

        const invalidate = (): void => {
            void queryClient.invalidateQueries({ queryKey: snapshotKeys.forCase(caseId) });
        };

        const templateKeyOf = (s: ViewportSnapshot): string | null =>
            s.templateConfigId && s.configId ? templateKey(s.templateConfigId, s.configId) : null;

        // SNAPSHOT_STORED: only user captures POST (template captures stay
        // ephemeral until the user edits them).
        const onStored = (snapshot: ViewportSnapshot): void => {
            if (snapshot.origin !== 'user') return;
            enqueue(snapshot.id, async () => {
                if (userIdMap.has(snapshot.id)) return;
                try {
                    const created = await withRetry(() =>
                        snapshots.create(caseId, {
                            metadata: buildUserMetadata(snapshot),
                            imageDataUrl: snapshot.dataUrl,
                            imageNoOverlayDataUrl: snapshot.dataUrlNoOverlay,
                        })
                    );
                    userIdMap.set(snapshot.id, created.id);
                    invalidate();
                } catch (err) {
                    reportFailure('save', err);
                }
            });
        };

        const onUpdated = (snapshot: ViewportSnapshot): void => {
            if (applying.has(snapshot.id)) return; // our own re-application echo

            if (snapshot.origin === 'user') {
                enqueue(snapshot.id, async () => {
                    const serverId = userIdMap.get(snapshot.id);
                    if (!serverId) return; // POST hasn't landed (or failed) — skip
                    try {
                        await withRetry(() => snapshots.update(caseId, serverId, buildPatch(snapshot)));
                        invalidate();
                    } catch (err) {
                        reportFailure('update', err);
                    }
                });
                return;
            }

            const key = templateKeyOf(snapshot);
            if (!key) return;
            enqueue(key, async () => {
                const serverId = templateMap.get(key);
                if (serverId) {
                    try {
                        const updated = await withRetry(() =>
                            snapshots.update(caseId, serverId, buildPatch(snapshot))
                        );
                        // Keep the mirror in step with the row so the next capture
                        // pass re-applies the edit the user just made.
                        templateRecords.set(key, updated);
                        invalidate();
                    } catch (err) {
                        reportFailure('update', err);
                    }
                    return;
                }
                // First edit of a not-yet-persisted template snapshot → POST (no image).
                const signature = snapshot.templateConfigId
                    ? (service.getTemplateCaptureResult(snapshot.templateConfigId)?.signature ?? undefined)
                    : undefined;
                try {
                    const created = await withRetry(() =>
                        snapshots.create(caseId, { metadata: buildTemplateMetadata(snapshot, signature) })
                    );
                    templateMap.set(key, created.id);
                    templateRecords.set(key, created);
                    clientKey.set(snapshot.id, key);
                    invalidate();
                } catch (err) {
                    reportFailure('save', err);
                }
            });
        };

        const onRemoved = ({ id }: { id: string }): void => {
            const userServerId = userIdMap.get(id);
            if (userServerId) {
                enqueue(id, async () => {
                    try {
                        await withRetry(() => snapshots.remove(caseId, userServerId));
                        userIdMap.delete(id);
                        invalidate();
                    } catch (err) {
                        reportFailure('delete', err);
                    }
                });
                return;
            }
            // Template snapshots cannot be deleted by the user — a REMOVED event for
            // one is always a capture pass clearing its own previous output. Drop the
            // dead store id but keep the persisted row and its mirror: the pass that
            // follows re-applies it, and a row anchored to stale data is discarded by
            // the signature check in onTemplateCaptured.
            clientKey.delete(id);
        };

        // Re-apply persisted annotations onto a freshly regenerated template
        // snapshot, guarded so the resulting SNAPSHOT_UPDATED is not re-PATCHed.
        const reApply = async (target: ViewportSnapshot, record: ISnapshotResponse): Promise<void> => {
            applying.add(target.id);
            try {
                if (record.includedInReport !== target.includedInReport) {
                    service.toggleReportInclusion(target.id);
                }
                if ((record.description ?? '') !== (target.description ?? '')) {
                    service.updateSnapshotDescription(target.id, record.description ?? '');
                }
                const annotations = record.annotations as unknown as AnnotationData[];
                if (annotations.length > 0) {
                    const baked = await bakeAnnotations(getSnapshotBaseUrl(target), annotations);
                    service.updateSnapshotAnnotations(target.id, annotations, baked, target.baseVariant);
                }
            } finally {
                applying.delete(target.id);
            }
        };

        // After a template capture pass, re-apply matching persisted annotations
        // (signature unchanged) or delete the stale record (signature changed).
        const onTemplateCaptured = ({
            templateId,
            ids,
            signature,
        }: {
            templateId: string;
            ids: string[];
            signature: string | null;
        }): void => {
            // Snapshots of this pass only — never an orphan from an earlier one.
            const captured = ids
                .map((id) => service.getSnapshot(id))
                .filter((s): s is ViewportSnapshot => s !== undefined);

            for (const [key, record] of templateRecords) {
                if (record.templateConfigId !== templateId) continue;

                if (record.captureSignature !== signature) {
                    // Stale: annotations anchored to outdated data → discard the row.
                    templateRecords.delete(key);
                    enqueue(key, async () => {
                        try {
                            await withRetry(() => snapshots.remove(caseId, record.id));
                            templateMap.delete(key);
                            invalidate();
                        } catch (err) {
                            reportFailure('discard', err);
                        }
                    });
                    continue;
                }

                // Kept (not consumed): every later pass re-applies it too, so an
                // in-session re-capture no longer drops the user's annotations.
                const target = captured.find((s) => s.configId === record.configId);
                if (!target) continue;
                templateMap.set(key, record.id);
                clientKey.set(target.id, key);
                void reApply(target, record);
            }
        };

        const unsubStored = service.subscribe(service.EVENTS.SNAPSHOT_STORED, onStored);
        const unsubUpdated = service.subscribe(service.EVENTS.SNAPSHOT_UPDATED, onUpdated);
        const unsubRemoved = service.subscribe(service.EVENTS.SNAPSHOT_REMOVED, onRemoved);
        const unsubTemplate = service.subscribe(service.EVENTS.TEMPLATE_CAPTURE_RECORDED, onTemplateCaptured);

        return () => {
            unsubStored();
            unsubUpdated();
            unsubRemoved();
            unsubTemplate();
        };
    }, [caseId, getServiceManager, api, queryClient]);

    // --- hydration ----------------------------------------------------------
    useEffect(() => {
        if (!records) return;
        if (hydratedRef.current === caseId) return;
        const isCaseChange = prevCaseRef.current !== null && prevCaseRef.current !== caseId;
        prevCaseRef.current = caseId;
        hydratedRef.current = caseId;

        const service = getServiceManager().getService<ViewportSnapshotService>(
            ViewportSnapshotService.SERVICE_NAME
        );
        const snapshots = api.snapshots;

        // On an actual case switch, drop maps left from the previous case
        // (configKeys can repeat across cases). Not on first mount — a capture
        // taken before hydration resolves must be preserved.
        if (isCaseChange) {
            userIdMapRef.current.clear();
            templateMapRef.current.clear();
            clientKeyRef.current.clear();
        }
        templateRecordsRef.current.clear();

        // Seed maps from metadata immediately (template re-application happens
        // when the report regenerates the snapshots).
        for (const r of records) {
            if (r.origin === 'user') {
                userIdMapRef.current.set(r.clientId, r.id);
            } else if (r.templateConfigId && r.configId) {
                const key = templateKey(r.templateConfigId, r.configId);
                templateRecordsRef.current.set(key, r);
                templateMapRef.current.set(key, r.id);
            }
        }

        // Restore user snapshots progressively — fetch each image, re-bake the
        // composite, then seed the store. Best-effort and idempotent.
        void Promise.all(
            records
                .filter((r) => r.origin === 'user')
                .map(async (r) => {
                    try {
                        const snapshot = await hydrateUserSnapshot(snapshots, caseId, r);
                        service.restoreSnapshot(snapshot);
                    } catch {
                        // skip an image that fails to load
                    }
                })
        );
    }, [records, caseId, getServiceManager, api]);
}

/** Rebuild a full user `ViewportSnapshot` from its persisted row + fetched images. */
async function hydrateUserSnapshot(
    snapshots: ReturnType<typeof useApi>['snapshots'],
    caseId: string,
    r: ISnapshotResponse
): Promise<ViewportSnapshot> {
    const dataUrl = await snapshots.getImage(caseId, r.id, 'overlay');
    const dataUrlNoOverlay = r.fileNoOverlayId
        ? await snapshots.getImage(caseId, r.id, 'no-overlay')
        : undefined;
    const annotations = r.annotations as unknown as AnnotationData[];
    const baseUrl = r.baseVariant === 'no-overlay' ? (dataUrlNoOverlay ?? dataUrl) : dataUrl;
    const annotatedDataUrl = annotations.length > 0 ? await bakeAnnotations(baseUrl, annotations) : undefined;
    return {
        id: r.clientId,
        viewMode: r.viewMode as ViewMode,
        viewportId: '',
        dataUrl,
        dataUrlNoOverlay,
        baseVariant: r.baseVariant,
        annotations,
        annotatedDataUrl,
        capturedAt: r.capturedAt,
        label: r.label ?? undefined,
        description: r.description ?? undefined,
        includedInReport: r.includedInReport,
        origin: 'user',
    };
}
